import { GetServerSideProps } from 'next';

import { sendCounterMetric } from 'utils/metrics';
import { SanityArticleData } from 'lib/sanity/types';
import { getPreviewArticle, getPublishedArticle, INVALID_MARKET, INVALID_SLUG } from 'lib/sanity';
import { cleanSlug, isPreviewSecretValidForArticleSlug } from 'utils/url-helpers/url-helpers';
import Article from 'components/Article';
import { trackException } from 'utils/exceptionHandler';
import { getSharedPageData } from 'utils/shared-page-data';

const getRedirectUrl = (slugInUrl: string, correctSlug: string, market: string, isPlain = false) => {
  if (slugInUrl === correctSlug) {
    return null;
  }
  // If the slug is outdated, we redirect to the new one
  return `/${market}/article/${correctSlug}/${isPlain ? 'plain/' : ''}`;
};

const getCMSData = async (articleSlug: string, market: string, previewSecret: string): Promise<SanityArticleData> => {
  const articleSlugLowerCase = articleSlug.toLowerCase();
  const isPreview =
    Boolean(previewSecret) && (await isPreviewSecretValidForArticleSlug(previewSecret, articleSlugLowerCase));
  let article;
  if (isPreview) {
    article = await getPreviewArticle(articleSlugLowerCase, market);
  } else {
    article = await getPublishedArticle(articleSlugLowerCase, market);
  }

  if (!article?.content) {
    throw new Error('404');
  }

  return article;
};

export const getServerSideProps: GetServerSideProps = async ({ req, res, query, resolvedUrl }) => {
  const market = query.market as string;
  const previewSecret = query.previewsecret as string;

  try {
    const articleSlug = cleanSlug(query.articleslug as string);

    const isPlain = resolvedUrl.endsWith('/plain/');

    const article = await getCMSData(articleSlug, market, previewSecret);

    const redirectUrl = getRedirectUrl(articleSlug, article.slugs[0], market, isPlain);
    if (redirectUrl) {
      sendCounterMetric('article-redirect', { redirect: 'redirect' });
      return {
        redirect: { destination: redirectUrl, permanent: false },
      };
    }
    sendCounterMetric('article-redirect', { redirect: 'no-redirect' });

    const sharedPageData = await getSharedPageData(req, market);
    return { props: { article, articleSlug, sharedPageData } };
  } catch (error) {
    if (error.message === '404' || error.message === INVALID_SLUG || error.message === INVALID_MARKET) {
      return {
        notFound: true,
      };
    }

    trackException(error, 'article-page', market);
    res.writeHead(500).end();
    return { props: {} };
  }
};

export default Article;
